import { changed, noOp, type Command, type CommandExecution } from './commands';
import { createId, type EditorDocument } from './model';
import type { SelectionState } from './selection';

export const ALPHA_CHANNELS_EXTENSION = 'maipo:alpha-channels';

export interface AlphaChannel {
  id: string;
  name: string;
  selection: SelectionState;
}

function validSelection(value: unknown): value is SelectionState {
  if (!value || typeof value !== 'object') return false;
  const selection = value as Partial<SelectionState>;
  return typeof selection.inverted === 'boolean' && Array.isArray(selection.regions)
    && selection.regions.every((region) => region && typeof region === 'object'
      && ['x', 'y', 'width', 'height'].every((key) => Number.isFinite((region as unknown as Record<string, unknown>)[key])));
}

function validChannel(value: unknown): value is AlphaChannel {
  if (!value || typeof value !== 'object') return false;
  const channel = value as Partial<AlphaChannel>;
  return typeof channel.id === 'string' && typeof channel.name === 'string' && validSelection(channel.selection);
}

export function alphaChannels(document: EditorDocument): AlphaChannel[] {
  const value = document.extensions[ALPHA_CHANNELS_EXTENSION];
  return Array.isArray(value) ? value.filter(validChannel).map((channel) => structuredClone(channel)) : [];
}

export function captureAlphaChannel(document: EditorDocument, name?: string): AlphaChannel {
  const channels = alphaChannels(document);
  return {
    id: createId('channel'),
    name: name?.trim() || `Alpha ${channels.length + 1}`,
    selection: {
      regions: document.selection.regions.map((region) => ({ ...region })),
      inverted: document.selection.inverted,
    },
  };
}

export class SetAlphaChannelsCommand implements Command {
  readonly label: string;
  private before?: unknown;
  private hadBefore = false;

  constructor(private readonly channels: readonly AlphaChannel[], label = 'Update alpha channels') {
    this.label = label;
  }

  retainedBytes(): number {
    return 128 + JSON.stringify(this.channels).length * 2 + JSON.stringify(this.before ?? null).length * 2;
  }

  execute(document: EditorDocument): CommandExecution {
    if (JSON.stringify(alphaChannels(document)) === JSON.stringify(this.channels)) return noOp();
    if (this.before === undefined) {
      this.hadBefore = Object.hasOwn(document.extensions, ALPHA_CHANNELS_EXTENSION);
      this.before = structuredClone(document.extensions[ALPHA_CHANNELS_EXTENSION] ?? null);
    }
    if (this.channels.length) document.extensions[ALPHA_CHANNELS_EXTENSION] = structuredClone(this.channels);
    else delete document.extensions[ALPHA_CHANNELS_EXTENSION];
    return changed();
  }

  undo(document: EditorDocument): void {
    if (this.hadBefore) document.extensions[ALPHA_CHANNELS_EXTENSION] = structuredClone(this.before);
    else delete document.extensions[ALPHA_CHANNELS_EXTENSION];
  }
}
